import { v4 as uuid } from "uuid";
import { HeaderPositionType, TextStyleType } from "../constants";
import { BaseCategory } from "./BaseCategory";
import { SlideCategories } from "./CategoryController";
import { SlideModelBuilder } from "./SlideModelBuilder";

const DAY_MS = 24 * 60 * 60 * 1000;

const STATUS_LABELS = {
    todo: "To Do",
    in_progress: "In Progress",
    blocked: "Blocked",
    done: "Done",
};

const STATUS_ORDER = ["todo", "in_progress", "blocked", "done"];

export class TasksCategory extends BaseCategory {
    get name() {
        return "Tasks";
    }

    get type() {
        return SlideCategories.TASKS;
    }

    get icon() {
        return null;
    }

    get description() {
        return "Use for execution planning and task tracking, including kanban boards, gantt charts, and structured task lists. If the prompt mentions kanban, gantt, task board, work-in-progress lanes, backlog, sprint tasks, or status columns, prefer this category. Best when content includes to-do/in-progress/blocked/done workflow lanes, owners, due dates, start/end dates, milestones, or multi-week/month/quarter project plans.";
    }

    get slideTypes() {
        return [];
    }

    getPrompt() {
        return `Create a task-planning slide.
        Provide 3-12 tasks and include structured fields when available.
        Use title as required. Include description, status, owner, start/end dates, or due date only when implied by the user input.
        Choose the most suitable task view based on the content: timeline, kanban board, or task list.`;
    }

    getImagePrompt() {
        return false;
    }

    getPromptSchema(options = {}) {
        if (options.preserveExactText) {
            return {
                tasks: [{
                    title: "exact task title",
                    description: "exact optional task details",
                    status: "exact optional status",
                    owner: "exact optional owner/assignee",
                    startDate: "exact optional task start date",
                    endDate: "exact optional task end date",
                    dueDate: "exact optional due date",
                }],
                preferredTaskView: "auto | timeline | board | list",
                summaryLine: "optional exact summary line",
            };
        }

        return {
            tasks: [{
                title: "required concise task title",
                description: "optional short task details",
                status: "optional status (e.g. To Do, In Progress, Blocked, Done)",
                owner: "optional owner or assignee",
                startDate: "optional start date",
                endDate: "optional end date",
                dueDate: "optional due date",
            }],
            preferredTaskView: "auto | timeline | board | list",
            summaryLine: "optional one-line summary",
        };
    }

    validateGeneratedModel(model) {
        const rawTasks = this.extractRawTasks(model);
        return Array.isArray(rawTasks) && rawTasks.length > 0;
    }

    getSlideModels(model) {
        const normalized = this.normalizeModel(model);
        const views = this.getOrderedViews(normalized);

        return views.map((view, index) => {
            switch (view) {
                case "timeline":
                    return this.buildGanttModel(normalized, index === 0);
                case "board":
                    return this.buildKanbanModel(normalized, index === 0);
                case "list":
                default:
                    return this.buildHorizontalTaskListModel(normalized, index === 0);
            }
        });
    }

    extractRawTasks(model) {
        return model?.tasks ?? model?.items ?? model?.steps ?? model?.nextSteps ?? [];
    }

    normalizeModel(model) {
        const rawTasks = this.extractRawTasks(model);
        const tasks = (Array.isArray(rawTasks) ? rawTasks : [])
            .map((task, index) => this.normalizeTask(task, index))
            .filter(task => task.title || task.description || task.owner)
            .slice(0, 12);

        const summaryLine = `${model?.summaryLine || model?.slideSubTitle || model?.conclusion || ""}`.trim();
        const preferredTaskView = this.normalizePreferredTaskView(model?.preferredTaskView);

        return {
            ...model,
            slideTitle: `${model?.slideTitle || "Task Plan"}`.trim() || "Task Plan",
            summaryLine,
            preferredTaskView,
            tasks,
        };
    }

    normalizeTask(task, index) {
        if (typeof task === "string") {
            return {
                id: uuid(),
                title: task.trim(),
                description: "",
                owner: "",
                status: "todo",
                originalStatus: "",
                startDate: null,
                endDate: null,
                dueDate: null,
                hasAnyDate: false,
                index,
            };
        }

        const title = `${task?.title || task?.name || task?.text || task?.task || task?.step || task?.action || ""}`.trim();
        const description = `${task?.description || task?.details || task?.detail || task?.notes || ""}`.trim();
        const owner = `${task?.owner || task?.assignee || task?.assignedTo || task?.responsible || ""}`.trim();
        const originalStatus = `${task?.status || task?.state || task?.column || ""}`.trim();
        const status = this.normalizeStatus(originalStatus);

        const startDate = this.parseDateValue(task?.startDate ?? task?.start);
        const endDate = this.parseDateValue(task?.endDate ?? task?.end);
        const dueDate = this.parseDateValue(task?.dueDate ?? task?.due ?? task?.deadline ?? task?.date);
        const hasAnyDate = Boolean(startDate || endDate || dueDate);

        return {
            id: uuid(),
            title,
            description,
            owner,
            status,
            originalStatus,
            startDate,
            endDate,
            dueDate,
            hasAnyDate,
            index,
        };
    }

    normalizePreferredTaskView(value) {
        const normalized = `${value || "auto"}`.toLowerCase().trim();
        if (["timeline", "board", "list", "auto"].includes(normalized)) {
            return normalized;
        }
        return "auto";
    }

    normalizeStatus(value) {
        const status = `${value || ""}`.toLowerCase().trim();
        if (!status) {
            return "todo";
        }
        if (/(done|complete|completed|closed|finished|resolved)/.test(status)) {
            return "done";
        }
        if (/(block|blocked|blocker|on hold|stuck|risk)/.test(status)) {
            return "blocked";
        }
        if (/(in progress|in-progress|doing|active|started|working)/.test(status)) {
            return "in_progress";
        }
        if (/(todo|to do|backlog|queued|not started|pending|planned)/.test(status)) {
            return "todo";
        }
        return "todo";
    }

    parseDateValue(value) {
        if (value == null || value === "") {
            return null;
        }

        if (typeof value === "number" && Number.isFinite(value)) {
            return value < 1e12 ? value * 1000 : value;
        }

        const parsed = Date.parse(`${value}`);
        return Number.isNaN(parsed) ? null : parsed;
    }

    getOrderedViews(model) {
        const explicitView = model.preferredTaskView;
        if (explicitView !== "auto") {
            return [
                explicitView,
                ...["timeline", "board", "list"].filter(view => view !== explicitView)
            ];
        }

        const hasDateSignals = model.tasks.some(task => task.hasAnyDate);
        const statusCount = model.tasks.filter(task => task.originalStatus.length > 0).length;
        const hasStatusSignals = statusCount >= Math.min(2, model.tasks.length) || statusCount >= Math.ceil(model.tasks.length / 2);

        if (hasDateSignals) {
            return ["timeline", "board", "list"];
        }
        if (hasStatusSignals) {
            return ["board", "list", "timeline"];
        }
        return ["list", "board", "timeline"];
    }

    buildGanttModel(model, isFirstChoice = false) {
        const builder = new SlideModelBuilder("gantt_chart", model, isFirstChoice);
        const scheduledTasks = this.inferScheduleForGantt(model.tasks);

        builder.createHeader({
            heading: model.slideTitle,
            subHeading: model.summaryLine,
            position: HeaderPositionType.TOP
        });

        builder.createPrimary({
            showGrid: true,
            showTaskDetail: "duration",
            axisScale: this.getAxisScale(scheduledTasks),
            isNormalized: true,
            milestones: this.buildGanttMilestones(scheduledTasks, builder),
            items: scheduledTasks.map(task => ({
                id: task.id,
                startDate: task.startDate,
                endDate: task.endDate,
                title: builder.createSingleBlockText(task.title || "Task", "_use_styles_"),
            })),
        });

        if (this.shouldRenderBottomTray(model)) {
            builder.createBottomTray(model.summaryLine);
        }

        return builder.model;
    }

    inferScheduleForGantt(tasks) {
        const baselineStart = this.startOfDay(Date.now());
        let fallbackCursor = baselineStart;

        return tasks.map(task => {
            let start = task.startDate;
            let end = task.endDate;

            if (!start && end) {
                start = end - DAY_MS;
            }
            if (!end && start) {
                end = start + DAY_MS;
            }
            if (!start && !end && task.dueDate) {
                end = task.dueDate;
                start = end - DAY_MS;
            }

            if (!start && !end) {
                start = fallbackCursor;
                end = start + DAY_MS;
                fallbackCursor += DAY_MS;
            }

            if (end < start) {
                end = start + DAY_MS;
            }

            return {
                ...task,
                startDate: this.startOfDay(start),
                endDate: this.endOfDay(end),
            };
        });
    }

    buildGanttMilestones(tasks, builder) {
        const byDate = new Map();
        tasks
            .filter(task => task.dueDate)
            .sort((a, b) => a.dueDate - b.dueDate)
            .forEach(task => {
                const date = this.startOfDay(task.dueDate);
                if (!byDate.has(date)) {
                    byDate.set(date, []);
                }
                byDate.get(date).push(task.title || "Task");
            });

        return Array.from(byDate.entries())
            .slice(0, 6)
            .map(([date, titles]) => {
                const dedupedTitles = Array.from(new Set(titles.map(title => `${title}`.trim()).filter(Boolean)));
                const [firstTitle, secondTitle] = dedupedTitles;
                let label = `${firstTitle || "Task"} due`;
                if (dedupedTitles.length > 1) {
                    label = `${firstTitle} +${dedupedTitles.length - 1} due`;
                } else if (!firstTitle && secondTitle) {
                    label = `${secondTitle} due`;
                }
                return {
                    id: uuid(),
                    date,
                    label: builder.createSingleBlockText(label, "_use_styles_"),
                    flipLabel: false,
                };
            });
    }

    shouldRenderBottomTray(model) {
        const summary = `${model?.summaryLine || ""}`.trim();
        if (!summary) {
            return false;
        }

        const heading = `${model?.slideTitle || ""}`.trim();
        const subHeading = `${model?.summaryLine || ""}`.trim();
        const normalizedSummary = summary.toLowerCase();

        if (normalizedSummary === heading.toLowerCase()) {
            return false;
        }
        if (normalizedSummary === subHeading.toLowerCase()) {
            return false;
        }

        return true;
    }

    getAxisScale(tasks) {
        if (!tasks.length) {
            return "days";
        }

        const minStart = Math.min(...tasks.map(task => task.startDate));
        const maxEnd = Math.max(...tasks.map(task => task.endDate));
        const rangeDays = (maxEnd - minStart) / DAY_MS;

        if (rangeDays <= 14) {
            return "days";
        }
        if (rangeDays <= 90) {
            return "weeks";
        }
        if (rangeDays <= 540) {
            return "months";
        }
        return "quarters";
    }

    buildKanbanModel(model, isFirstChoice = false) {
        const builder = new SlideModelBuilder("kanban", model, isFirstChoice);

        builder.createHeader({
            heading: model.slideTitle,
            subHeading: model.summaryLine,
            position: HeaderPositionType.TOP
        });

        const grouped = this.groupTasksByStatus(model.tasks);
        const laneStatuses = this.selectLaneStatuses(grouped);

        builder.createPrimary({
            showTitles: true,
            style: "box",
            taskLayout: "stack",
            items: laneStatuses.map(status => ({
                id: uuid(),
                label: STATUS_LABELS[status],
                items: grouped[status].map(task => this.createTaskCard(task, { includeStatus: false }, builder)),
            })),
        });

        builder.createBottomTray(model.summaryLine);

        return builder.model;
    }

    buildHorizontalTaskListModel(model, isFirstChoice = false) {
        const builder = new SlideModelBuilder("horizontal-task-list", model, isFirstChoice);

        builder.createHeader({
            heading: model.slideTitle,
            subHeading: model.summaryLine,
            position: HeaderPositionType.TOP
        });

        const grouped = this.groupTasksByStatus(model.tasks);
        const laneStatuses = this.selectLaneStatuses(grouped);

        builder.createPrimary({
            showTitles: true,
            itemStyle: "task",
            taskLayout: "fit",
            items: laneStatuses.map(status => ({
                id: uuid(),
                label: STATUS_LABELS[status],
                items: grouped[status].map(task => this.createTaskCard(task, { includeStatus: false, compact: true }, builder)),
            })),
        });

        builder.createBottomTray(model.summaryLine);

        return builder.model;
    }

    groupTasksByStatus(tasks) {
        const grouped = {
            todo: [],
            in_progress: [],
            blocked: [],
            done: [],
        };

        tasks.forEach(task => {
            grouped[task.status].push(task);
        });

        return grouped;
    }

    selectLaneStatuses(grouped) {
        const populatedStatuses = STATUS_ORDER.filter(status => grouped[status].length > 0);
        const laneStatuses = [...populatedStatuses];

        if (laneStatuses.length < 3) {
            STATUS_ORDER.forEach(status => {
                if (!laneStatuses.includes(status) && laneStatuses.length < 3) {
                    laneStatuses.push(status);
                }
            });
        }

        return laneStatuses;
    }

    createTaskCard(task, { includeStatus, compact = false }, builder) {
        const detailParts = [];
        if (!compact && task.description) {
            detailParts.push(task.description);
        }
        if (task.owner) {
            detailParts.push(`Owner: ${task.owner}`);
        }
        if (task.dueDate) {
            detailParts.push(`Due: ${new Date(task.dueDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`);
        }
        if (includeStatus && task.originalStatus) {
            detailParts.push(`Status: ${task.originalStatus}`);
        }

        return {
            id: task.id,
            text: {
                blocks: [
                    builder.createBlock(task.title || "Task", TextStyleType.TITLE),
                    builder.createBlock(detailParts.join(" | "), TextStyleType.BODY),
                ].filter(Boolean)
            }
        };
    }

    startOfDay(value) {
        const date = new Date(value);
        date.setHours(0, 0, 0, 0);
        return date.getTime();
    }

    endOfDay(value) {
        const date = new Date(value);
        date.setHours(23, 59, 59, 999);
        return date.getTime();
    }
}
